import type { ICoord2D, IRect, ISizeRect } from '@nova/types';

import { clampInt } from '../math-utils';
import {
  chooseAspectAwareGridDimensions,
  findNearestFreeRectOrigin,
  pointInRect,
  rectCenter,
  rectFitsMapWithMargin,
  rectOverlapsAny,
  rectsSeparatedByMargin
} from '../rect-utils';
import { randomIntInclusive, type SeededRandom } from '../seeded-random';

import { MAP_GEN_ZONE_JITTER_FRACTION } from './map-gen-defaults';
import { partitionInteriorByAnchorBox } from './partition-interior-by-anchor-box';

export type ZonedRectLayoutMode = 'random' | 'authored';

export interface PlaceZonedRectsArgs {
  mapWidth: number;
  mapHeight: number;
  stampSize: ISizeRect;
  /**
   * Inclusive randomized count range when auto-partitioning the map interior.
   * Ignored when `authoredOrigins` is set.
   */
  countMin?: number;
  countMax?: number;
  /**
   * Margin in tiles between this stamp's footprint and map edges and other POIs.
   * Default `0`.
   */
  spawnBufferTiles?: number;
  /** Already-reserved footprints. Stamps may not overlap these (subject to spawn buffers). */
  blocked: readonly IRect[];
  /** Spawn buffer per `blocked` entry; missing entries use `0`. */
  blockedSpawnBufferTiles?: readonly number[];
  /** Fixed stamp upper-left origins (map tile space). Skips zone partition. */
  authoredOrigins?: readonly ICoord2D[];
  /** Boundary jitter as a fraction of surplus slack (default {@link MAP_GEN_ZONE_JITTER_FRACTION}). */
  zoneJitterFraction?: number;
  /**
   * When auto-partitioning, reserve the zone cell containing this rect's center for a POI
   * (e.g. basecamp). Partition uses `stampCount + 1` zones; stamps only land in the others.
   */
  reservedZoneAnchor?: IRect;
  /** Partition rolls per stamp count when anchor-box zoning runs. Default 4. */
  partitionAttemptsPerCount?: number;
  /**
   * When false, only the rolled stamp count is attempted (no progressive reduction).
   * Default `true`.
   */
  allowCountReduction?: boolean;
  rand: SeededRandom;
  maxAttemptsPerStamp?: number;
}

export type PlaceZonedRectsResult = {
  ok: true;
  layoutMode: ZonedRectLayoutMode;
  requestedCount: number;
  /** Zones used for randomized placement; empty when authored. */
  zones: IRect[];
  stamps: IRect[];
  reducedFromRequested: boolean;
  /** Set when `reservedZoneAnchor` consumed one partition cell. */
  reservedZone?: IRect;
  /** Full partition cell list (anchor + pad zones) for debug overlays. */
  partitionCells?: IRect[];
} | {
  ok: false;
  reason: string;
};

/** Map bounds, spawn buffer, and reserved footprints for stamp collision checks. */
interface StampCollisionContext {
  mapWidth: number;
  mapHeight: number;
  buffer: number;
  blocked: readonly IRect[];
  blockedBuffers?: readonly number[];
}

interface StampFootprint {
  stampWidth: number;
  stampHeight: number;
}

/** Working state for one random stamp-count attempt (partition + place). */
interface RandomStampJob extends StampFootprint {
  args: PlaceZonedRectsArgs;
  stampCount: number;
  requestedCount: number;
  buffer: number;
  maxAttempts: number;
  jitter: number;
  partitionAttempts: number;
}

interface ResolvedPadZones {
  padZones: IRect[];
  reservedZone?: IRect;
}

interface PartitionRectIntoGridArgs {
  region: IRect;
  cols: number;
  rows: number;
  minCellWidth: number;
  minCellHeight: number;
  jitterFraction: number;
  rand: SeededRandom;
}

interface BuildAxisSizesArgs {
  total: number;
  parts: number;
  minPart: number;
  jitterFraction: number;
  rand: SeededRandom;
}

/**
 * Places equal-sized stamp rects, either at authored origins or randomly inside an aspect-aware
 * grid partition of the map interior.
 *
 * When the requested count cannot be placed, the randomized path retries with progressively fewer
 * stamps and reports that in `reducedFromRequested`, down to zero stamps, which still succeeds.
 */
export function placeZonedRects(args: PlaceZonedRectsArgs): PlaceZonedRectsResult {
  const footprint: StampFootprint = {
    stampWidth: Math.max(1, Math.floor(args.stampSize.width)),
    stampHeight: Math.max(1, Math.floor(args.stampSize.height))
  };
  const buffer = Math.max(0, Math.floor(args.spawnBufferTiles ?? 0));
  const maxAttempts = args.maxAttemptsPerStamp !== undefined
    ? Math.max(0, Math.floor(args.maxAttemptsPerStamp))
    : 80;

  if (args.authoredOrigins) {
    return placeAuthoredStamps(
      {
        mapWidth: args.mapWidth,
        mapHeight: args.mapHeight,
        buffer,
        blocked: args.blocked,
        blockedBuffers: args.blockedSpawnBufferTiles
      },
      footprint,
      args.authoredOrigins
    );
  }

  const countMin = Math.max(0, Math.floor(args.countMin ?? 0));
  const countMax = Math.max(countMin, Math.floor(args.countMax ?? countMin));
  if (!Number.isFinite(countMin) || !Number.isFinite(countMax)) {
    return {
      ok: false,
      reason: `Invalid stamp count range (${String(args.countMin)}..${String(args.countMax)}).`
    };
  }

  const requestedCount = randomIntInclusive(args.rand, countMin, countMax);
  const jitter = args.zoneJitterFraction ?? MAP_GEN_ZONE_JITTER_FRACTION;
  const partitionAttempts = args.partitionAttemptsPerCount !== undefined
    ? Math.max(0, Math.floor(args.partitionAttemptsPerCount))
    : 4;
  const allowCountReduction = args.allowCountReduction !== false;
  const minimumStampCount = allowCountReduction ? Math.max(0, countMin) : requestedCount;

  for (let stampCount = requestedCount; stampCount >= minimumStampCount; stampCount--) {
    if (stampCount === 0) {
      return {
        ok: true,
        layoutMode: 'random',
        requestedCount,
        zones: [],
        stamps: [],
        reducedFromRequested: requestedCount > 0
      };
    }

    const placed = tryPlaceStampCountWithPartitions({
      args,
      stampCount,
      requestedCount,
      stampWidth: footprint.stampWidth,
      stampHeight: footprint.stampHeight,
      buffer,
      maxAttempts,
      jitter,
      partitionAttempts
    });
    if (placed) {
      return placed;
    }
  }

  return {
    ok: false,
    reason:
      `Could not place any stamps (requested ${requestedCount}, stamp ${footprint.stampWidth}x${footprint.stampHeight}).`
  };
}

function jobCollision(job: RandomStampJob): StampCollisionContext {
  return {
    mapWidth: job.args.mapWidth,
    mapHeight: job.args.mapHeight,
    buffer: job.buffer,
    blocked: job.args.blocked,
    blockedBuffers: job.args.blockedSpawnBufferTiles
  };
}

function tryPlaceStampCountWithPartitions(job: RandomStampJob): PlaceZonedRectsResult | null {
  if (job.args.reservedZoneAnchor) {
    const anchorPartitioned = tryPlaceWithAnchorBoxPartition(job);
    if (anchorPartitioned) {
      return anchorPartitioned;
    }
  }

  return tryPlaceWithGridPartition(job);
}

function resolveReservedPadZones(
  args: PlaceZonedRectsArgs,
  zones: readonly IRect[],
  stampCount: number
): ResolvedPadZones | null {
  if (!args.reservedZoneAnchor) {
    if (zones.length !== stampCount) {
      return null;
    }
    return { padZones: [...zones] };
  }

  if (zones.length !== stampCount + 1) {
    return null;
  }

  const reservedIndex = pickZoneIndexContainingRectCenter(zones, args.reservedZoneAnchor);
  if (reservedIndex === null) {
    return null;
  }

  return {
    reservedZone: zones[reservedIndex],
    padZones: zones.filter((_, index) => index !== reservedIndex)
  };
}

function finalizePartitionedPlacement(
  job: RandomStampJob,
  placed: PlaceZonedRectsResult,
  reservedZone?: IRect,
  partitionCells?: readonly IRect[]
): PlaceZonedRectsResult | null {
  if (!placed.ok || placed.stamps.length !== job.stampCount) {
    return null;
  }

  const cells = partitionCells ??
    (reservedZone ? [reservedZone, ...placed.zones] : placed.zones);

  return {
    ...placed,
    reservedZone,
    partitionCells: [...cells]
  };
}

function tryPlaceWithAnchorBoxPartition(job: RandomStampJob): PlaceZonedRectsResult | null {
  const { args, stampCount, jitter, partitionAttempts } = job;
  const reservedZoneAnchor = args.reservedZoneAnchor;
  if (!reservedZoneAnchor) {
    return null;
  }

  const region = mapInteriorRegion(args.mapWidth, args.mapHeight);

  for (let partitionAttempt = 0; partitionAttempt < partitionAttempts; partitionAttempt++) {
    const partitioned = partitionInteriorByAnchorBox({
      region,
      anchor: reservedZoneAnchor,
      poiCellCount: stampCount + 1,
      minCellWidth: job.stampWidth,
      minCellHeight: job.stampHeight,
      jitterFraction: jitter,
      rand: args.rand
    });
    if (!partitioned) {
      continue;
    }

    const placed = placeStampsInZones(
      jobCollision(job),
      job,
      partitioned.padZones,
      args.rand,
      job.maxAttempts,
      job.requestedCount,
      job.stampCount < job.requestedCount
    );
    const finalized = finalizePartitionedPlacement(
      job,
      placed,
      partitioned.anchorZone,
      partitioned.allCells
    );
    if (finalized) {
      return finalized;
    }
  }

  return null;
}

function tryPlaceWithGridPartition(job: RandomStampJob): PlaceZonedRectsResult | null {
  const { args, stampCount, jitter, partitionAttempts } = job;

  for (let partitionAttempt = 0; partitionAttempt < partitionAttempts; partitionAttempt++) {
    const zoneSlotCount = args.reservedZoneAnchor ? stampCount + 1 : stampCount;
    const zones = partitionInteriorIntoGrid(
      args.mapWidth,
      args.mapHeight,
      zoneSlotCount,
      job,
      jitter,
      args.rand
    );

    const resolved = resolveReservedPadZones(args, zones, stampCount);
    if (!resolved) {
      continue;
    }

    const placed = placeStampsInZones(
      jobCollision(job),
      job,
      resolved.padZones,
      args.rand,
      job.maxAttempts,
      job.requestedCount,
      job.stampCount < job.requestedCount
    );
    const finalized = finalizePartitionedPlacement(job, placed, resolved.reservedZone);
    if (finalized) {
      return finalized;
    }
  }

  return null;
}

/** Full playable map bounds used as the partition region for random stamp placement. */
function mapInteriorRegion(mapWidth: number, mapHeight: number): IRect {
  return {
    x: 0,
    y: 0,
    width: mapWidth,
    height: mapHeight
  };
}

function placeAuthoredStamps(
  collision: StampCollisionContext,
  footprint: StampFootprint,
  authoredOrigins: readonly ICoord2D[]
): PlaceZonedRectsResult {
  const { stampWidth, stampHeight } = footprint;
  const requestedCount = authoredOrigins.length;
  const stamps: IRect[] = [];
  const blocked: IRect[] = [...collision.blocked];

  const minOriginX = collision.buffer;
  const minOriginY = collision.buffer;
  const maxOriginX = collision.mapWidth - stampWidth - collision.buffer;
  const maxOriginY = collision.mapHeight - stampHeight - collision.buffer;
  if (maxOriginX < minOriginX || maxOriginY < minOriginY) {
    return {
      ok: false,
      reason: `Stamp ${stampWidth}x${stampHeight} does not fit the map with spawn buffer ${collision.buffer}.`
    };
  }

  for (let i = 0; i < authoredOrigins.length; i++) {
    const preferred = authoredOrigins[i];
    const clamped = {
      x: clampInt(Math.floor(preferred.x), minOriginX, maxOriginX),
      y: clampInt(Math.floor(preferred.y), minOriginY, maxOriginY)
    };

    let origin = clamped;
    const candidate = stampRect(origin, stampWidth, stampHeight);

    if (!stampPlacementValid(collision, candidate, stamps)) {
      const relocated = findNearestFreeRectOrigin({
        width: stampWidth,
        height: stampHeight,
        maxOriginX,
        maxOriginY,
        preferred: clamped,
        blocked
      });
      if (!relocated) {
        return {
          ok: false,
          reason: `No non-overlapping authored stamp placement remains for index ${i}.`
        };
      }
      origin = {
        x: clampInt(relocated.x, minOriginX, maxOriginX),
        y: clampInt(relocated.y, minOriginY, maxOriginY)
      };
      const relocatedRect = stampRect(origin, stampWidth, stampHeight);
      if (!stampPlacementValid(collision, relocatedRect, stamps)) {
        return {
          ok: false,
          reason: `No non-overlapping authored stamp placement remains for index ${i}.`
        };
      }
    }

    const stamp = stampRect(origin, stampWidth, stampHeight);
    stamps.push(stamp);
    blocked.push(stamp);
  }

  return {
    ok: true,
    layoutMode: 'authored',
    requestedCount,
    zones: [],
    stamps,
    reducedFromRequested: false
  };
}

function placeStampsInZones(
  collision: StampCollisionContext,
  footprint: StampFootprint,
  zones: readonly IRect[],
  rand: SeededRandom,
  maxAttempts: number,
  requestedCount: number,
  reducedFromRequested: boolean
): PlaceZonedRectsResult {
  const stamps: IRect[] = [];

  for (let i = 0; i < zones.length; i++) {
    const placed = tryPlaceStampInZone(collision, footprint, zones[i], stamps, rand, maxAttempts);
    if (!placed) {
      return {
        ok: false,
        reason: `No valid stamp placement in zone ${i}.`
      };
    }
    stamps.push(placed);
  }

  return {
    ok: true,
    layoutMode: 'random',
    requestedCount,
    zones: [...zones],
    stamps,
    reducedFromRequested
  };
}

function partitionInteriorIntoGrid(
  mapWidth: number,
  mapHeight: number,
  stampCount: number,
  footprint: StampFootprint,
  zoneJitterFraction: number,
  rand: SeededRandom
): IRect[] {
  const region = mapInteriorRegion(mapWidth, mapHeight);
  if (region.width < 1 || region.height < 1) {
    return [];
  }

  const grid = chooseAspectAwareGridDimensions(stampCount, region.width, region.height);
  return partitionRectIntoGrid({
    region,
    cols: grid.col,
    rows: grid.row,
    minCellWidth: footprint.stampWidth,
    minCellHeight: footprint.stampHeight,
    jitterFraction: Math.max(0, zoneJitterFraction),
    rand
  });
}

function partitionRectIntoGrid(args: PartitionRectIntoGridArgs): IRect[] {
  const minW = args.minCellWidth * args.cols;
  const minH = args.minCellHeight * args.rows;
  if (args.region.width < minW || args.region.height < minH) {
    return [];
  }

  const widths = buildAxisSizes({
    total: args.region.width,
    parts: args.cols,
    minPart: args.minCellWidth,
    jitterFraction: args.jitterFraction,
    rand: args.rand
  });
  const heights = buildAxisSizes({
    total: args.region.height,
    parts: args.rows,
    minPart: args.minCellHeight,
    jitterFraction: args.jitterFraction,
    rand: args.rand
  });

  if (widths.length !== args.cols || heights.length !== args.rows) {
    return [];
  }

  const cells: IRect[] = [];
  let y = args.region.y;
  for (let row = 0; row < args.rows; row++) {
    let x = args.region.x;
    for (let col = 0; col < args.cols; col++) {
      cells.push({
        x,
        y,
        width: widths[col],
        height: heights[row]
      });
      x += widths[col];
    }
    y += heights[row];
  }
  return cells;
}

function buildAxisSizes(args: BuildAxisSizesArgs): number[] {
  const minTotal = args.minPart * args.parts;
  if (args.total < minTotal || args.parts < 1) {
    return [];
  }

  const slack = args.total - minTotal;
  const extras = distributeAxisSlack(slack, args.parts, args.jitterFraction, args.rand);
  return extras.map((extra) => args.minPart + extra);
}

/**
 * Splits integer slack across `parts` with random weights.
 * `jitterFraction` controls how uneven the weights can be (0 = even split).
 */
function distributeAxisSlack(
  slack: number,
  parts: number,
  jitterFraction: number,
  rand: SeededRandom
): number[] {
  if (parts < 1) {
    return [];
  }

  if (slack <= 0) {
    return Array.from({ length: parts }, () => 0);
  }

  const weights: number[] = [];
  let weightSum = 0;
  for (let i = 0; i < parts; i++) {
    const jitter = 1 + (((rand() * 2) - 1) * jitterFraction);
    const weight = Math.max(0.05, jitter);
    weights.push(weight);
    weightSum += weight;
  }

  const extras = weights.map((weight) => Math.floor((weight / weightSum) * slack));
  let assigned = extras.reduce((sum, value) => sum + value, 0);
  let cursor = 0;
  while (assigned < slack) {
    extras[cursor % parts] += 1;
    assigned += 1;
    cursor += 1;
  }
  return extras;
}

function tryPlaceStampInZone(
  collision: StampCollisionContext,
  footprint: StampFootprint,
  zone: IRect,
  placedStamps: readonly IRect[],
  rand: SeededRandom,
  maxAttempts: number
): IRect | null {
  const { stampWidth, stampHeight } = footprint;
  const maxOriginX = zone.x + zone.width - stampWidth;
  const maxOriginY = zone.y + zone.height - stampHeight;
  if (maxOriginX < zone.x || maxOriginY < zone.y) {
    return null;
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const origin: ICoord2D = {
      x: randomIntInclusive(rand, zone.x, maxOriginX),
      y: randomIntInclusive(rand, zone.y, maxOriginY)
    };
    const stamp = stampRect(origin, stampWidth, stampHeight);

    if (!stampPlacementValid(collision, stamp, placedStamps)) {
      continue;
    }

    return stamp;
  }

  return null;
}

function stampPlacementValid(
  collision: StampCollisionContext,
  stamp: IRect,
  placedStamps: readonly IRect[]
): boolean {
  if (!rectFitsMapWithMargin(collision.mapWidth, collision.mapHeight, stamp, collision.buffer)) {
    return false;
  }

  if (rectOverlapsAny(stamp, collision.blocked)) {
    return false;
  }

  for (let i = 0; i < collision.blocked.length; i++) {
    const blockedBuffer = collision.blockedBuffers?.[i] ?? 0;
    if (!rectsSeparatedByMargin(stamp, collision.buffer, collision.blocked[i], blockedBuffer)) {
      return false;
    }
  }

  for (const placed of placedStamps) {
    if (!rectsSeparatedByMargin(stamp, collision.buffer, placed, collision.buffer)) {
      return false;
    }
  }

  return true;
}

function stampRect(origin: ICoord2D, width: number, height: number): IRect {
  return {
    x: origin.x,
    y: origin.y,
    width,
    height
  };
}

function pickZoneIndexContainingRectCenter(zones: readonly IRect[], anchor: IRect): number | null {
  const center = rectCenter(anchor);
  const index = zones.findIndex((zone) => pointInRect(center, zone));
  return index === -1 ? null : index;
}
